import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Instagram, Facebook, Youtube, Music2 } from 'lucide-react';
import { Input, Textarea, Label } from './ui/Form';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: '',
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast({
        title: 'Salamat for reaching out!',
        description: "We'll get back to you within 24 hours.",
      });
      setForm({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' });
    } catch (err) {
      toast({
        title: 'Could not send',
        description: 'Please try again or message us on Facebook.',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-offwhite">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="overline text-muted mb-4">Contact Us</p>
            <h2 className="editorial-display text-pond text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8">
              Let's plan
              <br />
              <em className="italic font-light text-moss">your day.</em>
            </h2>

            <div className="space-y-6 text-charcoal/85">
              <div className="flex gap-4">
                <MapPin className="w-5 h-5 mt-1 text-pond flex-shrink-0" />
                <div>
                  <p className="overline text-muted mb-1">Location</p>
                  <p className="text-base">
                    Sitio Asana, Ubihan
                    <br />
                    City of Meycauayan, 3020
                    <br />
                    Bulacan, Philippines
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Phone className="w-5 h-5 mt-1 text-pond flex-shrink-0" />
                <div>
                  <p className="overline text-muted mb-1">Call / Viber</p>
                  <a href="tel:+639170000000" className="text-base hover:text-pond" data-testid="contact-phone">
                    +63 917 000 0000
                  </a>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail className="w-5 h-5 mt-1 text-pond flex-shrink-0" />
                <div>
                  <p className="overline text-muted mb-1">Email</p>
                  <a href="mailto:hellopo@asanafishingresort.com" className="text-base hover:text-pond break-all" data-testid="contact-email">
                    hellopo@asanafishingresort.com
                  </a>
                </div>
              </div>
              <div className="flex gap-3 pt-2 flex-wrap">
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-instagram"
                  aria-label="Instagram"
                  className="w-11 h-11 rounded-full border border-moss/40 flex items-center justify-center hover:bg-pond hover:text-offwhite hover:border-pond transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-facebook"
                  aria-label="Facebook"
                  className="w-11 h-11 rounded-full border border-moss/40 flex items-center justify-center hover:bg-pond hover:text-offwhite hover:border-pond transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-youtube"
                  aria-label="YouTube"
                  className="w-11 h-11 rounded-full border border-moss/40 flex items-center justify-center hover:bg-pond hover:text-offwhite hover:border-pond transition-colors"
                >
                  <Youtube className="w-5 h-5" />
                </a>
                <a
                  href="https://tiktok.com"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="social-tiktok"
                  aria-label="TikTok"
                  className="w-11 h-11 rounded-full border border-moss/40 flex items-center justify-center hover:bg-pond hover:text-offwhite hover:border-pond transition-colors"
                >
                  <Music2 className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          <motion.form
            onSubmit={submit}
            data-testid="inquiry-form"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 rounded-3xl bg-sand border border-moss/25 p-7 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <Label htmlFor="iname">Name</Label>
                <Input id="iname" required data-testid="inquiry-name" value={form.name} onChange={set('name')} placeholder="Your name" />
              </div>
              <div>
                <Label htmlFor="iemail">Email</Label>
                <Input id="iemail" required type="email" data-testid="inquiry-email" value={form.email} onChange={set('email')} placeholder="you@email.com" />
              </div>
              <div>
                <Label htmlFor="iphone">Phone (optional)</Label>
                <Input id="iphone" data-testid="inquiry-phone" value={form.phone} onChange={set('phone')} placeholder="+63 ..." />
              </div>
              <div>
                <Label htmlFor="isubject">Subject</Label>
                <Input id="isubject" data-testid="inquiry-subject" value={form.subject} onChange={set('subject')} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="imessage">Message</Label>
                <Textarea
                  id="imessage"
                  required
                  data-testid="inquiry-message"
                  rows={4}
                  value={form.message}
                  onChange={set('message')}
                  placeholder="Tell us about your group, your dates, or any questions..."
                />
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row justify-end gap-4">
              <Button type="submit" size="lg" disabled={loading} data-testid="inquiry-submit">
                {loading ? 'Sending…' : 'Send Inquiry'}
              </Button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
